/* eslint-disable no-constant-condition */
/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from "react";
import DatePicker from "react-datepicker";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context/auth";
import { getClientes, getEdificios } from "../../utils";
import { client } from "../../utils/supabase";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "./stylesCarteleriaForm.scss";

const CarteleriaForm = ({ id, onSuccess }) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      tipo_vertical: "",
      referencia_id: "",
      incluye_hardware: false,
      modalidad: "CAPEX",
      monto_hardware: "",
      cuota_hardware_opex: "",
      fee_gestion_unitario: "",
      incluye_contenidos: false,
      fee_contenidos_unitario: "",
      moneda_hardware: "",
      moneda_gestion: "",
      moneda_contenidos: "",
      plazo_contrato_meses: "",
      cantidad_pantallas: "",
      fecha_inicio: "",
      estado: "",
      pais: "",
    },
  });

  const [clientes, setClientes] = useState([]);
  const [edificios, setEdificios] = useState([]);
  const loadedRefIdRef = useRef(null);
  const [fechaInicio, setFechaInicio] = useState(new Date());
  const [feeGestion, setFeeGestion] = useState("");
  const [montoHardware, setMontoHardware] = useState("");
  const [cuotaOpex, setCuotaOpex] = useState("");
  const [feeContenidos, setFeeContenidos] = useState("");
  const [fechaCierre, setFechaCierre] = useState(null);

  const { pais, id: userId } = useAuthContext();

  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";

  const estadoOptions = ["Activo", "100%", "90%", "75%", "50%"];

  const getMonedaOptions = useCallback(() => (pais === "Chile" ? ["UF", "USD", "CLP"] : ["USD", "PEN"]), [pais]);

  const getDefaultMoneda = () =>
    pais === "Chile"
      ? { gestion: "UF", hardware: "CLP", contenidos: "UF" }
      : { gestion: "PEN", hardware: "PEN", contenidos: "PEN" };

  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const parseInputNumber = (value) => {
    if (!value || value === "") return 0;
    const num = parseFloat(value.toString().replace(/,/g, "."));
    return isNaN(num) ? 0 : num;
  };

  const tipoVertical = watch("tipo_vertical");
  const labelReferencia = tipoVertical === "EO" ? "Edificio" : tipoVertical === "OT" ? "Cliente o Edificio" : "Cliente";

  useEffect(() => {
    fetchClientes();
    fetchEdificios();
    if (id) fetchNegocio(id);

    const defaults = getDefaultMoneda();
    setValue("moneda_gestion", defaults.gestion);
    setValue("moneda_hardware", defaults.hardware);
    setValue("moneda_contenidos", defaults.contenidos);
    setValue("modalidad", "CAPEX"); // CAPEX preseleccionado
    if (normalizedPais) setValue("pais", normalizedPais);
  }, [id, pais, setValue, normalizedPais]);

  // Re-aplicar referencia_id cuando las listas de opciones estén disponibles
  useEffect(() => {
    if (loadedRefIdRef.current && (edificios.length > 0 || clientes.length > 0)) {
      setValue("referencia_id", loadedRefIdRef.current);
    }
  }, [edificios, clientes, setValue]);

  useEffect(() => {
    if (!watch("incluye_hardware")) {
      setValue("modalidad", "CAPEX");
      setValue("monto_hardware", "");
      setValue("cuota_hardware_opex", "");
    }
    if (!watch("incluye_contenidos")) {
      setValue("fee_contenidos_unitario", "");
    }
  }, [watch("incluye_hardware"), watch("incluye_contenidos"), setValue]);

  useEffect(() => {
    if (tipoVertical === "OT") {
      setValue("plazo_contrato_meses", null);
      setValue("cantidad_pantallas", null);
      setValue("fee_gestion_unitario", null);
      setValue("incluye_contenidos", false);
      setValue("fee_contenidos_unitario", null);
      setValue("estado", ""); // deja vacío para que el usuario elija
    }
  }, [tipoVertical, setValue]);

  const fetchClientes = async () => {
    try {
      const data = await getClientes({ pais, orderBy: "nombre", ascending: true });
      setClientes(data?.sort((a, b) => a.nombre?.localeCompare(b.nombre) || 0) || []);
    } catch (error) {
      console.error("Error fetching clientes:", error);
      toast.error("Error al cargar clientes");
      setClientes([]);
    }
  };

  const fetchEdificios = async () => {
    try {
      const data = await getEdificios({ pais });
      setEdificios(data?.sort((a, b) => a.edificio?.localeCompare(b.edificio) || 0) || []);
    } catch (error) {
      console.error("Error fetching edificios:", error);
      toast.error("Error al cargar edificios");
      setEdificios([]);
    }
  };

  const fetchNegocio = async (id) => {
    try {
      const { data, error } = await client.from("negocios_carteleria").select("*").eq("id", id).single();
      if (error) throw error;

      const defaults = getDefaultMoneda();
      setFeeGestion(data.fee_gestion_unitario?.toString().replace(".", ",") || "");
      setMontoHardware(data.monto_hardware?.toString().replace(".", ",") || "");
      setCuotaOpex(data.cuota_hardware_opex?.toString().replace(".", ",") || "");
      setFeeContenidos(data.fee_contenidos_unitario?.toString().replace(".", ",") || "");

      reset({
        ...data,
        plazo_contrato_meses: data.plazo_contrato_meses || "",
        cantidad_pantallas: data.cantidad_pantallas || "",
        pais: data.pais || normalizedPais,
        estado: estadoOptions.includes(data.estado) ? data.estado : "",
        incluye_hardware: data.incluye_hardware || false,
        incluye_contenidos: data.incluye_contenidos || false,
        modalidad: data.modalidad || "CAPEX",
        moneda_hardware: data.moneda_hardware || defaults.hardware,
        moneda_gestion: data.moneda_gestion || defaults.gestion,
        moneda_contenidos: data.moneda_contenidos || defaults.contenidos,
        ejecutivo: data.ejecutivo || "",
        // Eliminamos campos de cuotas
        cuota_hardware_opex: data.modalidad === "OPEX" ? data.cuota_hardware_opex : "",
      });

      // Guardar referencia_id para re-aplicar cuando las listas carguen
      loadedRefIdRef.current = data.referencia_id;

      // Parsear como hora local (no UTC) para evitar desplazamiento de un día
      if (data.fecha_inicio) {
        const [y, m, d] = data.fecha_inicio.split("-");
        setFechaInicio(new Date(y, m - 1, d));
      }
      if (data.fecha_cierre) {
        const [y, m, d] = data.fecha_cierre.split("-");
        setFechaCierre(new Date(y, m - 1, d));
      } else {
        setFechaCierre(null);
      }
    } catch (error) {
      console.error("Error fetching negocio:", error);
      toast.error("Error al cargar oportunidad");
    }
  };

  const onSubmit = async (data) => {
    if (!normalizedPais) {
      toast.error("No se ha definido el país del usuario");
      return;
    }

    // Validaciones de montos
    if (data.tipo_vertical !== "OT") {
      if (parseInputNumber(feeGestion) <= 0) {
        toast.error("El fee de gestión debe ser mayor a 0");
        return;
      }
      if (data.incluye_contenidos && parseInputNumber(feeContenidos) <= 0) {
        toast.error("El fee de contenidos debe ser mayor a 0");
        return;
      }
    }
    if (data.incluye_hardware) {
      if (data.modalidad === "CAPEX" && parseInputNumber(montoHardware) <= 0) {
        toast.error("El monto de hardware CAPEX debe ser mayor a 0");
        return;
      }
      if (data.modalidad === "OPEX" && parseInputNumber(cuotaOpex) <= 0) {
        toast.error("La cuota OPEX debe ser mayor a 0");
        return;
      }
    }
    // fecha_cierre (cierre hardware/CPE) y fecha_inicio (inicio servicio/gestión)
    // son independientes: el hardware puede cerrarse antes de que inicie el servicio

    // Solo generar nuevo ID al crear, no al editar
    let nextId = null;
    if (!id) {
      const { data: maxIdData, error: maxIdError } = await client
        .from("negocios_carteleria")
        .select("id")
        .order("id", { ascending: false })
        .limit(1);

      nextId = maxIdError || maxIdData.length === 0 ? 1 : parseInt(maxIdData[0].id) + 1;

      while (true) {
        const { data: checkData, error: checkError } = await client
          .from("negocios_carteleria")
          .select("id")
          .eq("id", nextId.toString())
          .maybeSingle();
        if (checkError || !checkData) break;
        nextId += 1;
      }
    }

    const defaults = getDefaultMoneda();

    const submitData = {
      ...(nextId ? { id: nextId.toString() } : {}),
      ...data,
      fecha_inicio: formatDateLocal(fechaInicio),
      monto_hardware: data.incluye_hardware ? parseInputNumber(montoHardware) : 0,
      cuota_hardware_opex: data.incluye_hardware && data.modalidad === "OPEX" ? parseInputNumber(cuotaOpex) : 0,
      fee_gestion_unitario: parseInputNumber(feeGestion),
      fee_contenidos_unitario: data.incluye_contenidos ? parseInputNumber(feeContenidos) : 0,
      plazo_contrato_meses: data.plazo_contrato_meses ? parseInt(data.plazo_contrato_meses) : null,
      cantidad_pantallas: data.cantidad_pantallas ? parseInt(data.cantidad_pantallas) : null,
      pais: normalizedPais,
      ejecutivo: id ? data.ejecutivo : userId ? String(userId) : null,
      moneda_gestion: data.moneda_gestion || defaults.gestion,
      moneda_hardware: data.moneda_hardware || defaults.hardware,
      moneda_contenidos: data.moneda_contenidos || defaults.contenidos,
      fecha_cierre: fechaCierre ? formatDateLocal(fechaCierre) : null,
    };

    // Limpieza específica para proyectos tipo "Otros"
    if (data.tipo_vertical === "OT") {
      submitData.fee_gestion_unitario = null;
      submitData.moneda_gestion = null;
      submitData.incluye_contenidos = false;
      submitData.fee_contenidos_unitario = null;
      submitData.moneda_contenidos = null;
      submitData.plazo_contrato_meses = null;
      submitData.cantidad_pantallas = null;
    } else {
      // Para EO y TD: limpiar según modalidad
      if (submitData.modalidad === "OPEX" && data.incluye_hardware) {
        submitData.monto_hardware = 0;
      } else if (data.incluye_hardware) {
        submitData.cuota_hardware_opex = 0;
      }
    }

    try {
      const { error } = id
        ? await client.from("negocios_carteleria").update(submitData).eq("id", id).select()
        : await client.from("negocios_carteleria").insert([submitData]).select();

      if (error) throw error;

      toast.success("Oportunidad guardada exitosamente");
      if (onSuccess) onSuccess();

      const defaultMoneda = pais === "Chile" ? "UF" : "PEN";
      reset({
        tipo_vertical: "",
        referencia_id: "",
        incluye_hardware: false,
        modalidad: "CAPEX",
        monto_hardware: "",
        cuota_hardware_opex: "",
        fee_gestion_unitario: "",
        incluye_contenidos: false,
        fee_contenidos_unitario: "",
        moneda_hardware: defaultMoneda,
        moneda_gestion: defaultMoneda,
        moneda_contenidos: defaultMoneda,
        plazo_contrato_meses: "",
        cantidad_pantallas: "",
        fecha_inicio: "",
        estado: "100%",
        nombre_proyecto: "",
        pais: normalizedPais,
      });

      setFechaInicio(new Date());
      setFeeGestion("");
      setMontoHardware("");
      setCuotaOpex("");
      setFeeContenidos("");
      setFechaCierre(null);
    } catch (error) {
      console.error("Error saving negocio:", error);
      toast.error("Error al guardar: " + error.message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="carteleria-form">
      {/* Tipo de Negocio */}
      <div className="mb-3">
        <label className="form-label">Vertical</label>
        <select
          {...register("tipo_vertical", { required: "Selecciona un tipo de vertical" })}
          className="form-select"
          onChange={(e) => {
            setValue("tipo_vertical", e.target.value);
            setValue("referencia_id", ""); // limpia la referencia al cambiar
          }}
        >
          <option value="">Selecciona...</option>
          <option value="EO">Edificios de Oficinas</option>
          <option value="TD">Transformación Digital</option>
          <option value="OT">Otros</option>
        </select>
        {errors.tipo_vertical && <span className="text-danger">{errors.tipo_vertical.message}</span>}
      </div>

      {/* Referencia */}
      <div className="mb-3">
        <label className="form-label">{labelReferencia}</label>
        <select
          {...register("referencia_id", {
            required: "Selecciona una referencia (edificio o cliente)",
          })}
          className="form-select"
          disabled={!tipoVertical}
        >
          <option value="">Selecciona...</option>
          {tipoVertical === "EO" &&
            edificios.map((edificio) => (
              <option key={edificio.id} value={edificio.id}>
                [Edificio] {edificio.edificio || `ID ${edificio.id}`}
              </option>
            ))}
          {tipoVertical === "TD" &&
            clientes.map((cliente) => (
              <option key={cliente.id} value={cliente.id}>
                [Cliente] {cliente.nombre || `ID ${cliente.id}`}
              </option>
            ))}
          {tipoVertical === "OT" && (
            <>
              {/* Edificios */}
              {edificios.map((edificio) => (
                <option key={`ed-${edificio.id}`} value={edificio.id}>
                  [Edificio] {edificio.edificio || `ID ${edificio.id}`}
                </option>
              ))}
              {/* Clientes */}
              {clientes.map((cliente) => (
                <option key={`cl-${cliente.id}`} value={cliente.id}>
                  [Cliente] {cliente.nombre || `ID ${cliente.id}`}
                </option>
              ))}
            </>
          )}
        </select>
        {tipoVertical === "OT" && (
          <div className="mb-3">
            <label className="form-label">
              Descripción del proyecto <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              {...register("nombre_proyecto", {
                required: "La descripción es obligatoria para Otros",
              })}
              className="form-control"
              placeholder="Ej: Venta equipos usados, Reutilización pantallas ex-proyecto, etc."
            />
            {errors.nombre_proyecto && <span className="text-danger">{errors.nombre_proyecto.message}</span>}
          </div>
        )}
        {errors.referencia_id && <span className="text-danger">{errors.referencia_id.message}</span>}
      </div>

      {/* Plazo + Cantidad */}
      {tipoVertical !== "OT" && (
        <div className="row mb-3">
          <div className="col-md-6">
            <label className="form-label">Plazo (meses)</label>
            <input
              type="text"
              placeholder="-"
              {...register("plazo_contrato_meses", {
                required: tipoVertical !== "OT" ? "Ingresa el plazo" : false,
                pattern: { value: /^\d+$/, message: "Solo enteros" },
                validate: (v) => {
                  if (tipoVertical === "OT") return true;
                  return parseInt(v) >= 1 || "Mínimo 1";
                },
              })}
              className="form-control no-arrows"
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                setValue("plazo_contrato_meses", val);
              }}
            />
            {errors.plazo_contrato_meses && <span className="text-danger">{errors.plazo_contrato_meses.message}</span>}
          </div>
          <div className="col-md-6">
            <label className="form-label">Pantallas</label>
            <input
              type="text"
              placeholder="-"
              {...register("cantidad_pantallas", {
                required: tipoVertical !== "OT" ? "Ingresa la cantidad" : false,
                pattern: { value: /^\d+$/, message: "Solo enteros" },
                validate: (v) => {
                  if (tipoVertical === "OT") return true;
                  return parseInt(v) >= 1 || "Mínimo 1";
                },
              })}
              className="form-control no-arrows"
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                setValue("cantidad_pantallas", val);
              }}
            />
            {errors.cantidad_pantallas && <span className="text-danger">{errors.cantidad_pantallas.message}</span>}
          </div>
        </div>
      )}

      {/* === FECHA Y ESTADO EN LA MISMA FILA === */}
      {/* === FECHA INICIO (solo visible en EO y TD) + ESTADO === */}
      <div className="row mb-3">
        {tipoVertical !== "OT" && (
          <div className="col-md-6">
            <label className="form-label">Fecha Inicio</label>
            <DatePicker
              selected={fechaInicio}
              onChange={setFechaInicio}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              required
            />
          </div>
        )}
        <div className={tipoVertical === "OT" ? "col-md-12" : "col-md-6"}>
          <label className="form-label">Estado</label>
          <select
            {...register("estado", {
              required: tipoVertical === "OT" ? "Selecciona el estado de la venta" : "Selecciona estado",
            })}
            className="form-select"
          >
            <option value="">Selecciona...</option>
            {tipoVertical === "OT" ? (
              <>
                <option value="50%">50%</option>
                <option value="75%">75%</option>
                <option value="90%">90%</option>
                <option value="100%">100%</option>
              </>
            ) : (
              estadoOptions.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))
            )}
          </select>
          {errors.estado && <span className="text-danger">{errors.estado.message}</span>}
        </div>
      </div>

      {tipoVertical !== "OT" && (
        <div className="block-section">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold", fontSize: "15px", margin: 0 }}>Gestión</span>
          </div>
          <div className="input-row">
            <div className="input-group">
              <input
                type="text"
                placeholder="Fee unitario"
                value={feeGestion}
                className="form-control"
                onChange={(e) => {
                  setFeeGestion(e.target.value);
                  setValue("fee_gestion_unitario", e.target.value, { shouldDirty: true });
                }}
                onBlur={() => {
                  const raw = feeGestion || "";
                  const clean = raw.replace(/\./g, ",").replace(/[^\d,]/g, "");
                  const parts = clean.split(",");
                  let formatted = "";
                  if (parts.length > 1) {
                    const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
                    const decPart = parts[1].padEnd(2, "0").slice(0, 2);
                    formatted = intPart + "," + decPart;
                  } else if (parts[0]) {
                    const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
                    formatted = intPart + ",00";
                  }
                  setFeeGestion(formatted);
                  setValue("fee_gestion_unitario", formatted, { shouldDirty: true });
                }}
              />
              <select {...register("moneda_gestion", { required: true })} className="form-select">
                {getMonedaOptions().map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {errors.fee_gestion_unitario && <span className="text-danger">{errors.fee_gestion_unitario.message}</span>}
        </div>
      )}

      {/* === HARDWARE === */}
      <div className="block-section">
        {/* 1. Solo checkbox + label */}
        <div style={{ marginBottom: "12px" }}>
          <div className="form-check" style={{ margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>
            <input
              type="checkbox"
              {...register("incluye_hardware")}
              id="incluye_hardware"
              className="form-check-input"
              onChange={(e) => {
                const checked = e.target.checked;
                setValue("incluye_hardware", checked);
                setValue("modalidad", "CAPEX");
                if (!checked) {
                  setValue("monto_hardware", "");
                  setValue("cuota_hardware_opex", "");
                  setFechaCierre(null);
                }
              }}
            />
            <label
              className="form-check-label"
              htmlFor="incluye_hardware"
              style={{ margin: 0, fontWeight: "bold", fontSize: "15px" }}
            >
              Hardware
            </label>
          </div>
        </div>

        {/* 2. Modalidad + Moneda → 50/50 */}
        {watch("incluye_hardware") && (
          <div className="row mb-3">
            <div className="col-md-6">
              <select
                {...register("modalidad", { required: watch("incluye_hardware") })}
                className="form-select"
                onChange={(e) => {
                  setValue("modalidad", e.target.value);
                  if (e.target.value === "OPEX") setValue("monto_hardware", "");
                  else setValue("cuota_hardware_opex", "");
                }}
              >
                <option value="CAPEX">CAPEX</option>
                <option value="OPEX">OPEX</option>
              </select>
            </div>
            <div className="col-md-6">
              <select {...register("moneda_hardware", { required: watch("incluye_hardware") })} className="form-select">
                {getMonedaOptions().map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* 3. Monto/Cuota + Fecha Cierre → 50/50 */}
        {watch("incluye_hardware") && (
          <div className="row mb-3">
            <div className="col-md-6">
              {watch("modalidad") === "CAPEX" ? (
                <input
                  type="text"
                  placeholder="0,00"
                  value={montoHardware}
                  className="form-control"
                  onChange={(e) => {
                    setMontoHardware(e.target.value);
                    setValue("monto_hardware", e.target.value, { shouldDirty: true });
                  }}
                  onBlur={() => {
                    const raw = montoHardware || "";
                    const clean = raw.replace(/\./g, ",").replace(/[^\d,]/g, "");
                    const parts = clean.split(",");
                    let formatted = "";
                    if (parts.length > 1) {
                      const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
                      const decPart = parts[1].padEnd(2, "0").slice(0, 2);
                      formatted = intPart + "," + decPart;
                    } else if (parts[0]) {
                      formatted = parts[0] + ",00";
                    }
                    setMontoHardware(formatted);
                    setValue("monto_hardware", formatted, { shouldDirty: true });
                  }}
                />
              ) : (
                <input
                  type="text"
                  placeholder="0,00"
                  value={cuotaOpex}
                  className="form-control"
                  onChange={(e) => {
                    setCuotaOpex(e.target.value);
                    setValue("cuota_hardware_opex", e.target.value, { shouldDirty: true });
                  }}
                  onBlur={() => {
                    const raw = cuotaOpex || "";
                    const clean = raw.replace(/\./g, ",").replace(/[^\d,]/g, "");
                    const parts = clean.split(",");
                    let formatted = "";
                    if (parts.length > 1) {
                      const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
                      const decPart = parts[1].padEnd(2, "0").slice(0, 2);
                      formatted = intPart + "," + decPart;
                    } else if (parts[0]) {
                      formatted = parts[0] + ",00";
                    }
                    setCuotaOpex(formatted);
                    setValue("cuota_hardware_opex", formatted, { shouldDirty: true });
                  }}
                />
              )}
            </div>

            <div className="col-md-6">
              <DatePicker
                selected={fechaCierre}
                onChange={(date) => setFechaCierre(date)}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                placeholderText="Seleccionar fecha"
                isClearable
                disabled={!watch("incluye_hardware")}
              />
            </div>
          </div>
        )}
      </div>

      {/* === CONTENIDOS === */}
      {tipoVertical !== "OT" && (
        <div className="block-section">
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
            <div className="form-check" style={{ margin: 0 }}>
              <input
                type="checkbox"
                {...register("incluye_contenidos")}
                id="incluye_contenidos"
                className="form-check-input"
                onChange={(e) => {
                  const checked = e.target.checked;
                  setValue("incluye_contenidos", checked);
                  if (!checked) setValue("fee_contenidos_unitario", "");
                }}
              />
              <label
                className="form-check-label"
                htmlFor="incluye_contenidos"
                style={{ margin: 0, fontWeight: "bold", fontSize: "15px" }}
              >
                Contenidos
              </label>
            </div>
          </div>
          <div className="input-row">
            <div className="input-group">
              <input
                type="text"
                placeholder="Fee unitario"
                value={feeContenidos}
                className="form-control"
                disabled={!watch("incluye_contenidos")}
                onChange={(e) => {
                  setFeeContenidos(e.target.value);
                  setValue("fee_contenidos_unitario", e.target.value, { shouldDirty: true });
                }}
                onBlur={() => {
                  const raw = feeContenidos || "";
                  const clean = raw.replace(/\./g, ",").replace(/[^\d,]/g, "");
                  const parts = clean.split(",");
                  let formatted = "";
                  if (parts.length > 1) {
                    const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
                    const decPart = parts[1].padEnd(2, "0").slice(0, 2);
                    formatted = intPart + "," + decPart;
                  } else if (parts[0]) {
                    const intPart = parts[0].replace(/^0+(?=\d)/, "") || "0";
                    formatted = intPart + ",00";
                  }
                  setFeeContenidos(formatted);
                  setValue("fee_contenidos_unitario", formatted, { shouldDirty: true });
                }}
              />
              <select
                {...register("moneda_contenidos", { required: watch("incluye_contenidos") })}
                className="form-select"
                disabled={!watch("incluye_contenidos")}
              >
                {getMonedaOptions().map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <input type="hidden" {...register("ejecutivo")} />

      <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
        {isSubmitting ? "Guardando..." : "Guardar"}
      </button>
    </form>
  );
};

export default CarteleriaForm;
